//
import { goRequest } from '../utils/httpClientTs';
import { GenericResponse } from '../types/generic';
import {
  DocumentList,
  DocumentEditView,
  DocumentView,
  DocumentCreate,
  DocumentUpdate,
  DocumentVersionCreate,
  DocumentVersionUpdate,
  DocumentBundleCreate,
  DocumentBundleUpdate,
  DocumentBundleDocumentCreate,
  DocumentBundleUpdateDocument,
  DocumentVersion,
  DocumentBundle,
  DocumentBundleListItem,
  DocumentCategory,
  DocumentSubCategory,
  PaginatedDocumentResponse,
} from '../types/document';

// API endpoints based on the types/document.ts file
export const getDocumentList = async (context: {
  search?: string;
  categories?: string[];
  sub_categories?: string[];
  whats_new?: boolean;
  page?: number;
  per_page?: number;
}): Promise<PaginatedDocumentResponse> => {
  let newParams: any = {
    search: context.search,
    categories: null,
    sub_categories: null,
    page: context.page || 1,
    per_page: context.per_page || 20,
  };
  if (context.categories) {
    newParams.categories = context.categories.join(',');
  }
  if (context.sub_categories) {
    newParams.sub_categories = context.sub_categories.join(',');
  }
  // Only include whats_new in params if it's true
  if (context.whats_new === true) {
    newParams.whats_new = true;
  }
  return goRequest({
    url: '/library/documents',
    method: 'GET',
    params: newParams,
  });
};

export const getDocumentEditView = async (
  id: string
): Promise<DocumentEditView> => {
  return goRequest({
    url: `/library/document/${id}/manage`,
    method: 'GET',
  });
};

export const getDocumentView = async (
  id: string,
  versionId: string
): Promise<DocumentView> => {
  return goRequest({
    url: `/library/document/${id}/view/${versionId}`,
    method: 'GET',
  });
};

export const getDocumentVersion = async (
  id: string,
  versionId: string
): Promise<DocumentVersion> => {
  return goRequest({
    url: `/library/document/${id}/manage/${versionId}`,
    method: 'GET',
  });
};

export const createDocument = async (
  data: DocumentCreate
): Promise<GenericResponse> => {
  return goRequest({
    url: '/library/documents',
    method: 'POST',
    data,
  });
};

export const updateDocument = async (
  id: string,
  data: DocumentUpdate
): Promise<GenericResponse> => {
  return goRequest({
    url: `/library/document/${id}/manage`,
    method: 'PUT',
    data,
  });
};

export const deleteDocument = async (id: string): Promise<GenericResponse> => {
  return goRequest({
    url: `/library/document/${id}/manage`,
    method: 'DELETE',
  });
};

export const createDocumentVersion = async (
  id: string,
  data: DocumentVersionCreate
): Promise<GenericResponse> => {
  return goRequest({
    url: `/library/document/${id}/manage`,
    method: 'POST',
    data,
  });
};

export const updateDocumentVersion = async (
  id: string,
  versionId: string,
  data: DocumentVersionUpdate
): Promise<GenericResponse> => {
  return goRequest({
    url: `/library/document/${id}/manage/${versionId}`,
    method: 'PUT',
    data,
  });
};

export const getDocumentBundle = async (
  bundleId: string
): Promise<DocumentBundle> => {
  return goRequest({
    url: `/library/bundle/${bundleId}`,
    method: 'GET',
  });
};

export const createDocumentBundle = async (
  data: DocumentBundleCreate
): Promise<GenericResponse> => {
  return goRequest({
    url: '/library/bundle',
    method: 'POST',
    data,
  });
};

export const updateDocumentBundle = async (
  id: string,
  data: DocumentBundleUpdate
): Promise<GenericResponse> => {
  return goRequest({
    url: `/library/bundle/${id}`,
    method: 'PUT',
    data,
  });
};

export const addDocumentToBundle = async (
  bundleId: string,
  data: DocumentBundleDocumentCreate
): Promise<GenericResponse> => {
  return goRequest({
    url: `/library/bundle/${bundleId}`,
    method: 'POST',
    data,
  });
};

export const updateBundleDocument = async (
  bundleId: string,
  data: DocumentBundleUpdateDocument
): Promise<GenericResponse> => {
  return goRequest({
    url: `/library/bundle/${bundleId}`,
    method: 'PATCH',
    data,
  });
};

export const getBundleList = async (
  search?: string
): Promise<DocumentBundleListItem[]> => {
  return goRequest({
    url: '/library/bundles',
    method: 'GET',
    params: search ? { search } : undefined,
  });
};

export const getCategories = async (): Promise<DocumentCategory[]> => {
  return goRequest({
    url: '/library/categories',
    method: 'GET',
  });
};

export const getSubCategories = async (): Promise<DocumentSubCategory[]> => {
  return goRequest({
    url: '/library/sub_categories',
    method: 'GET',
  });
};

// Mocked functions for document upload and removal as requested
export const uploadDocument = async (
  file: File,
  documentId: string,
  versionId: string
): Promise<{ success: boolean; message: string }> => {
  // Mock implementation - in real implementation, this would upload the file
  console.log(
    `Mock uploading file ${file.name} for document ${documentId}, version ${versionId}`
  );

  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 1000));

  return {
    success: true,
    message: `File ${file.name} uploaded successfully`,
  };
};

export const removeDocumentFile = async (
  documentId: string,
  versionId: string
): Promise<{ success: boolean; message: string }> => {
  // Mock implementation - in real implementation, this would remove the file
  console.log(
    `Mock removing file for document ${documentId}, version ${versionId}`
  );

  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 500));

  return {
    success: true,
    message: 'Document file removed successfully',
  };
};

// Legacy functions for backward compatibility (can be removed once all components are updated)
export const getDocuments = getDocumentList;
export const getDocumentEditList = async (params: {
  search?: string;
  categories?: string[];
  sub_categories?: string[];
  whats_new?: boolean;
  page?: number;
  per_page?: number;
}): Promise<
  PaginatedDocumentResponse & {
    data: (DocumentList & {
      categories: string[];
      sub_categories: string[];
      tags: string[];
      active_versions: string[];
    })[];
  }
> => {
  const response = await getDocumentList(params);
  // This function needs to be updated to match the new API structure
  // For now, returning empty arrays for the additional fields
  const enhancedDocuments = response.data.map((doc) => ({
    ...doc,
    categories: [],
    sub_categories: [],
    tags: [],
    active_versions: [],
  }));

  return {
    ...response,
    data: enhancedDocuments,
  };
};

export const getDocumentVersionDetail = async (
  id: string,
  versionId: string
): Promise<DocumentVersion> => {
  return getDocumentVersion(id, versionId);
};

export const getIRSDocument = async (data: { id: string }): Promise<any> => {
  return goRequest({
    url: `/library/irs/${data.id}`,
    method: 'GET',
  });
};

export const downloadDocument = async (
  id: string,
  versionId: string
): Promise<Blob> => {
  return goRequest({
    url: `/library/${id}/download/${versionId}`,
    method: 'GET',
    responseType: 'blob',
  });
};

// Document version file operations
export const downloadDocumentVersion = async (
  documentId: string,
  version: string
): Promise<{ url: string }> => {
  // Returns a presigned URL for viewing/downloading the document
  // Backend endpoint: /library/document/<doc_id>/document/<version_id>/url
  return goRequest({
    url: `/library/document/${documentId}/document/${version}/url`,
    method: 'GET',
  });
};

export const downloadDocumentVersionBlob = async (
  documentId: string,
  version: string
): Promise<{ blob: Blob; filename?: string; contentType?: string }> => {
  // Returns a blob for direct download with metadata
  // Backend endpoint: /library/document/<doc_id>/document/<version_id>/download
  const response = await goRequest({
    url: `/library/document/${documentId}/document/${version}/download`,
    method: 'GET',
    responseType: 'blob',
  });

  // Extract filename from Content-Disposition header if available
  const contentDisposition = response.headers?.['content-disposition'];
  let filename: string | undefined;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    );
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }

  // Get content type from response headers
  const contentType = response.headers?.['content-type'] || response.type;

  return {
    blob: response,
    filename,
    contentType,
  };
};

export const uploadDocumentVersion = async (
  documentId: string,
  version: string,
  file: File
): Promise<{ success: boolean; message: string }> => {
  const formData = new FormData();
  formData.append('file', file);

  return goRequest({
    url: `/library/document/${documentId}/document/${version}`,
    method: 'POST',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const deleteDocumentVersion = async (
  documentId: string,
  version: string
): Promise<{ success: boolean; message: string }> => {
  return goRequest({
    url: `/library/document/${documentId}/document/${version}`,
    method: 'DELETE',
  });
};

// Utility function to get file extension from content type
export const getFileExtensionFromContentType = (
  contentType: string
): string => {
  const mimeToExt: { [key: string]: string } = {
    'application/pdf': '.pdf',
    'image/jpeg': '.jpg',
    'image/jpg': '.jpg',
    'image/png': '.png',
    'image/gif': '.gif',
    'image/webp': '.webp',
    'image/svg+xml': '.svg',
    'text/plain': '.txt',
    'text/html': '.html',
    'text/css': '.css',
    'text/javascript': '.js',
    'application/json': '.json',
    'application/xml': '.xml',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      '.docx',
    'application/vnd.ms-excel': '.xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      '.xlsx',
    'application/vnd.ms-powerpoint': '.ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      '.pptx',
    'video/mp4': '.mp4',
    'video/webm': '.webm',
    'video/ogg': '.ogv',
    'audio/mpeg': '.mp3',
    'audio/wav': '.wav',
    'audio/ogg': '.oga',
  };

  return mimeToExt[contentType.toLowerCase()] || '';
};

// Download entire bundle as zip file
export const downloadBundle = async (bundleId: string): Promise<Blob> => {
  const response = await goRequest({
    url: `/library/bundle/${bundleId}/download`,
    method: 'GET',
    responseType: 'blob',
  });
  return response;
};

// Document Permissions API endpoints
export const getDocumentPermissions = async (
  documentId: string
): Promise<{ group_type_ids: string[] }> => {
  return goRequest({
    url: `/library/document/${documentId}/permissions`,
    method: 'GET',
  });
};

export const setDocumentPermissions = async (
  documentId: string,
  groupTypeIds: string[]
): Promise<GenericResponse> => {
  return goRequest({
    url: `/library/document/${documentId}/permissions`,
    method: 'POST',
    data: { group_type_ids: groupTypeIds },
  });
};

export const removeDocumentPermissions = async (
  documentId: string
): Promise<GenericResponse> => {
  return goRequest({
    url: `/library/document/${documentId}/permissions`,
    method: 'DELETE',
  });
};
